import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_BASE = `/api`;
const ACCENT = '#6366f1';           // indigo-500 (matches --primary)
const SIDEBAR_BG = '#1e293b';       // dark navy – always dark sidebar
const SIDEBAR_WIDTH = 220;

function relativeTime(isoString) {
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const EVENT_COLORS = {
  login:          { bg: '#d1fae5', color: '#065f46' },
  login_failed:   { bg: '#fee2e2', color: '#991b1b' },
  user_created:   { bg: '#dbeafe', color: '#1e40af' },
  user_updated:   { bg: '#fef3c7', color: '#92400e' },
  user_deleted:   { bg: '#fee2e2', color: '#991b1b' },
  password_reset: { bg: '#ede9fe', color: '#5b21b6' },
  chat_message:   { bg: '#ccfbf1', color: '#0f766e' },
};

const Toast = ({ message, type, onDismiss }) => {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  const bgMap = { success: '#22c55e', error: '#ef4444', info: ACCENT };
  return (
    <div style={{
      background: bgMap[type] || bgMap.info, color: '#fff', padding: '12px 20px', borderRadius: 10,
      boxShadow: '0 8px 30px rgba(0,0,0,0.18)', fontSize: 14, fontWeight: 500,
      display: 'flex', alignItems: 'center', gap: 10, animation: 'slideIn 0.25s ease',
      maxWidth: 340, pointerEvents: 'auto',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>&#x2715;</button>
    </div>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 8888, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)',
  }}>
    <div style={{
      background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480,
      boxShadow: '0 24px 64px rgba(0,0,0,0.22)', animation: 'fadeUp 0.2s ease', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 24px', borderBottom: '1px solid #f0f0f0', background: '#fafafa',
      }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111' }}>{title}</h3>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#888',
          lineHeight: 1, padding: '2px 6px', borderRadius: 6,
        }}
          onMouseEnter={e => e.currentTarget.style.background = '#eee'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >&#x2715;</button>
      </div>
      <div style={{ padding: '24px' }}>{children}</div>
    </div>
  </div>
);

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  border: '1.5px solid var(--color-muted)',
  borderRadius: 8,
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s',
  color: 'var(--color-text)',
  background: 'var(--color-surface)',
};
const btnPrimary = {
  background: ACCENT, color: '#fff', border: 'none', padding: '9px 20px',
  borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
  transition: 'background 0.15s', fontFamily: 'system-ui, -apple-system, sans-serif',
};
const btnSecondary = {
  background: '#f1f5f9', color: '#374151', border: 'none', padding: '9px 20px',
  borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500,
  transition: 'background 0.15s', fontFamily: 'system-ui, -apple-system, sans-serif',
};
const btnDanger = {
  background: '#fee2e2', color: '#dc2626', border: 'none', padding: '6px 12px',
  borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
  transition: 'background 0.15s', fontFamily: 'system-ui, -apple-system, sans-serif',
};
const btnAction = {
  background: '#eff6ff', color: ACCENT, border: 'none', padding: '6px 12px',
  borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
  transition: 'background 0.15s', fontFamily: 'system-ui, -apple-system, sans-serif',
};
const cardStyle = {
  background: '#fff', borderRadius: 14,
  boxShadow: '0 1px 4px rgba(0,0,0,0.07), 0 4px 20px rgba(0,0,0,0.04)', padding: '24px',
};
const labelStyle = { display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' };

const StatCard = ({ icon, label, value, accent }) => (
  <div style={{
    ...cardStyle, flex: 1, minWidth: 150, display: 'flex', flexDirection: 'column', gap: 8,
    borderTop: `3px solid ${accent || ACCENT}`, transition: 'transform 0.15s, box-shadow 0.15s',
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.10)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07), 0 4px 20px rgba(0,0,0,0.04)'; }}
  >
    <span style={{ fontSize: 26 }}>{icon}</span>
    <div style={{ fontSize: 28, fontWeight: 800, color: '#111', letterSpacing: '-1px' }}>{value ?? '-'}</div>
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{label}</div>
  </div>
);

const BarChart = ({ data }) => {
  if (!data || data.length === 0) return <div style={{ color: '#aaa', fontSize: 13 }}>No data</div>;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 140, padding: '0 4px' }}>
      {data.map((d, i) => {
        const pct = (d.count / max) * 100;
        const shortDate = d.date ? d.date.slice(5) : '';
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{d.count}</div>
            <div title={`${d.date}: ${d.count}`} style={{
              width: '100%', height: `${Math.max(pct, 4)}%`,
              background: `linear-gradient(180deg, ${ACCENT} 0%, #3b74e0 100%)`,
              borderRadius: '4px 4px 0 0', transition: 'height 0.4s ease', minHeight: 4,
            }} />
            <div style={{ fontSize: 10, color: '#9ca3af', textAlign: 'center', whiteSpace: 'nowrap' }}>{shortDate}</div>
          </div>
        );
      })}
    </div>
  );
};

const Th = ({ children, align }) => (
  <th style={{
    padding: '12px 16px', textAlign: align || 'left', fontSize: 12, fontWeight: 700,
    color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
  }}>{children}</th>
);
const Td = ({ children, align }) => (
  <td style={{ padding: '12px 16px', textAlign: align || 'left', color: '#374151' }}>{children}</td>
);
const FormField = ({ label, children }) => (
  <div><label style={labelStyle}>{label}</label>{children}</div>
);
const ModalFooter = ({ onClose, onSubmit, submitting, label }) => (
  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
    <button style={btnSecondary} onClick={onClose} disabled={submitting}
      onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
      onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
    >Cancel</button>
    <button style={{ ...btnPrimary, opacity: submitting ? 0.7 : 1 }} onClick={onSubmit} disabled={submitting}
      onMouseEnter={e => !submitting && (e.currentTarget.style.background = '#3b74e0')}
      onMouseLeave={e => e.currentTarget.style.background = ACCENT}
    >{submitting ? 'Saving...' : label}</button>
  </div>
);
const LoadingSpinner = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80, color: '#aaa', gap: 12, fontSize: 15 }}>
    <div style={{ width: 28, height: 28, border: `3px solid #e5e7eb`, borderTopColor: ACCENT, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    Loading...
  </div>
);
const ErrorBanner = ({ message, onRetry }) => (
  <div style={{
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
    padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, color: '#dc2626',
  }}>
    <span style={{ fontSize: 20 }}>&#9888;</span>
    <span style={{ flex: 1, fontSize: 14 }}>{message}</span>
    {onRetry && <button style={{ ...btnPrimary, background: '#dc2626', fontSize: 13 }} onClick={onRetry}>Retry</button>}
  </div>
);

// OVERVIEW SECTION
const OverviewSection = ({ apiCall }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true); setError(null);
    try { const data = await apiCall(`${API_BASE}/admin/stats`); setStats(data); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [apiCall]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} onRetry={fetchStats} />;
  if (!stats) return null;
  const last7 = (stats.sessions_per_day || []).slice(-7);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <StatCard icon="&#128100;" label="Total Users"    value={stats.total_users}        accent="#4f8ef7" />
        <StatCard icon="&#128172;" label="Total Sessions" value={stats.total_sessions}     accent="#10b981" />
        <StatCard icon="&#128232;" label="Total Messages" value={stats.total_messages}     accent="#f59e0b" />
        <StatCard icon="&#128994;" label="Active Today"   value={stats.active_users_today} accent="#8b5cf6" />
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ ...cardStyle, flex: 2, minWidth: 280 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 16 }}>Sessions per Day (Last 7 Days)</div>
          <BarChart data={last7} />
        </div>
        <div style={{ ...cardStyle, flex: 1, minWidth: 200, borderTop: '3px solid #f59e0b', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 40 }}>&#129689;</span>
          <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 600, textAlign: 'center' }}>Estimated Token Usage</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#111', letterSpacing: '-1px' }}>
            {stats.total_tokens_estimated != null ? stats.total_tokens_estimated.toLocaleString() : '-'}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>tokens (approx.)</div>
        </div>
      </div>
      <div style={cardStyle}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 16 }}>&#127942; Top Users by Messages</div>
        {stats.top_users && stats.top_users.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ borderBottom: '2px solid #f0f0f0' }}><Th>Rank</Th><Th>Username</Th><Th>Full Name</Th><Th align="right">Messages</Th></tr></thead>
            <tbody>
              {stats.top_users.map((u, i) => (
                <tr key={u.username} style={{ borderBottom: '1px solid #f8f8f8' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Td><span style={{
                    display: 'inline-flex', width: 24, height: 24, borderRadius: '50%',
                    alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                    background: i === 0 ? '#fef3c7' : i === 1 ? '#f1f5f9' : '#fdf4ff',
                    color: i === 0 ? '#92400e' : i === 1 ? '#374151' : '#7c3aed',
                  }}>{i + 1}</span></Td>
                  <Td><span style={{ fontWeight: 600, color: ACCENT }}>@{u.username}</span></Td>
                  <Td>{u.full_name || '-'}</Td>
                  <Td align="right"><span style={{ fontWeight: 700, color: '#111' }}>{u.message_count}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: 24 }}>No data available</div>
        )}
      </div>
    </div>
  );
};

// USERS SECTION
const UsersSection = ({ apiCall, showToast }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [sessions, setSessions] = useState({});
  const [sessionsLoading, setSessionsLoading] = useState({});
  const fileInputRef = useRef(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true); setError(null);
    try { const data = await apiCall(`${API_BASE}/admin/users`); setUsers(Array.isArray(data) ? data : []); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [apiCall]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openModal = (type, user = null) => {
    setModal({ type, user });
    if (type === 'create') setFormData({ username: '', password: '', full_name: '', is_admin: false });
    else if (type === 'edit') setFormData({ full_name: user.full_name || '', is_admin: !!user.is_admin });
    else if (type === 'reset') setFormData({ new_password: '' });
  };
  const closeModal = () => { setModal(null); setFormData({}); };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (modal.type === 'create') {
        await apiCall(`${API_BASE}/admin/users`, { method: 'POST', body: JSON.stringify(formData) });
        showToast('User created successfully', 'success');
      } else if (modal.type === 'edit') {
        await apiCall(`${API_BASE}/admin/users/${modal.user.id}`, { method: 'PUT', body: JSON.stringify(formData) });
        showToast('User updated successfully', 'success');
      } else if (modal.type === 'reset') {
        await apiCall(`${API_BASE}/admin/users/${modal.user.id}/reset-password`, { method: 'POST', body: JSON.stringify(formData) });
        showToast('Password reset successfully', 'success');
      }
      closeModal(); fetchUsers();
    } catch (e) { showToast(e.message || 'Operation failed', 'error'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.username}"? This cannot be undone.`)) return;
    try { await apiCall(`${API_BASE}/admin/users/${user.id}`, { method: 'DELETE' }); showToast('User deleted', 'success'); fetchUsers(); }
    catch (e) { showToast(e.message || 'Delete failed', 'error'); }
  };

  const toggleSessions = async (user) => {
    if (expandedUser === user.id) { setExpandedUser(null); return; }
    setExpandedUser(user.id);
    if (sessions[user.id]) return;
    setSessionsLoading(prev => ({ ...prev, [user.id]: true }));
    try {
      const data = await apiCall(`${API_BASE}/admin/users/${user.id}/sessions`);
      setSessions(prev => ({ ...prev, [user.id]: Array.isArray(data) ? data : [] }));
    } catch (e) { showToast('Failed to load sessions', 'error'); }
    finally { setSessionsLoading(prev => ({ ...prev, [user.id]: false })); }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try { await apiCall(`${API_BASE}/admin/users/upload`, { method: 'POST', body: fd, isFormData: true }); showToast('CSV uploaded successfully', 'success'); fetchUsers(); }
    catch (e) { showToast(e.message || 'Upload failed', 'error'); }
    finally { if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const filtered = users.filter(u =>
    u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} onRetry={fetchUsers} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: 16 }}>&#128269;</span>
          <input style={{ ...inputStyle, paddingLeft: 36 }} placeholder="Search users..." value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={e => e.target.style.borderColor = ACCENT}
            onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
        </div>
        <button style={btnPrimary} onClick={() => openModal('create')}
          onMouseEnter={e => e.currentTarget.style.background = '#3b74e0'}
          onMouseLeave={e => e.currentTarget.style.background = ACCENT}
        >+ Create User</button>
        <button style={{ ...btnSecondary, display: 'flex', alignItems: 'center', gap: 6 }}
          onClick={() => fileInputRef.current?.click()}
          onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
          onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
        >&#128228; Upload CSV</button>
        <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvUpload} />
      </div>
      <div style={{ fontSize: 13, color: '#6b7280' }}>
        Showing <strong>{filtered.length}</strong> of <strong>{users.length}</strong> users
      </div>
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f0f0f0' }}>
              <Th>ID</Th><Th>Username</Th><Th>Full Name</Th><Th>Role</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>No users found</td></tr>
            )}
            {filtered.map(user => (
              <React.Fragment key={user.id}>
                <tr style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Td><span style={{ color: '#9ca3af', fontSize: 12 }}>#{user.id}</span></Td>
                  <Td><span style={{ fontWeight: 600, color: '#111' }}>@{user.username}</span></Td>
                  <Td>{user.full_name || <span style={{ color: '#ccc' }}>-</span>}</Td>
                  <Td>
                    <span style={{
                      display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: user.is_admin ? '#ede9fe' : '#f1f5f9',
                      color: user.is_admin ? '#7c3aed' : '#374151',
                    }}>{user.is_admin ? 'Admin' : 'User'}</span>
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={btnAction} onClick={() => openModal('edit', user)}
                        onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
                        onMouseLeave={e => e.currentTarget.style.background = '#eff6ff'}
                      >&#9998; Edit</button>
                      <button style={btnAction} onClick={() => openModal('reset', user)}
                        onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
                        onMouseLeave={e => e.currentTarget.style.background = '#eff6ff'}
                      >&#128273; Reset</button>
                      <button style={{ ...btnAction, background: '#f0fdf4', color: '#16a34a' }}
                        onClick={() => toggleSessions(user)}
                        onMouseEnter={e => e.currentTarget.style.background = '#dcfce7'}
                        onMouseLeave={e => e.currentTarget.style.background = '#f0fdf4'}
                      >{expandedUser === user.id ? '▲ Hide' : '▼ Sessions'}</button>
                      <button style={btnDanger} onClick={() => handleDelete(user)}
                        onMouseEnter={e => e.currentTarget.style.background = '#fecaca'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}
                      >&#128465; Delete</button>
                    </div>
                  </Td>
                </tr>
                {expandedUser === user.id && (
                  <tr>
                    <td colSpan={5} style={{ background: '#f8faff', padding: '12px 24px', borderBottom: '1px solid #e5e7eb' }}>
                      {sessionsLoading[user.id] ? (
                        <div style={{ color: '#aaa', fontSize: 13, padding: '8px 0' }}>Loading sessions...</div>
                      ) : sessions[user.id]?.length > 0 ? (
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Sessions for @{user.username}
                          </div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead><tr style={{ borderBottom: '1px solid #e5e7eb' }}><Th>ID</Th><Th>Title</Th><Th>Created</Th></tr></thead>
                            <tbody>
                              {sessions[user.id].map(s => (
                                <tr key={s.id}>
                                  <Td><span style={{ color: '#9ca3af', fontSize: 11 }}>#{s.id}</span></Td>
                                  <Td>{s.title || <span style={{ color: '#ccc' }}>Untitled</span>}</Td>
                                  <Td style={{ color: '#6b7280', fontSize: 12 }}>{s.created_at ? relativeTime(s.created_at) : '-'}</Td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={{ color: '#aaa', fontSize: 13, padding: '4px 0' }}>No sessions found.</div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {modal?.type === 'create' && (
        <Modal title="+ Create User" onClose={closeModal}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormField label="Username *">
              <input style={inputStyle} placeholder="johndoe" value={formData.username || ''}
                onChange={e => setFormData(p => ({ ...p, username: e.target.value }))}
                onFocus={e => e.target.style.borderColor = ACCENT} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </FormField>
            <FormField label="Password *">
              <input style={inputStyle} type="password" placeholder="••••••••" value={formData.password || ''}
                onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                onFocus={e => e.target.style.borderColor = ACCENT} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </FormField>
            <FormField label="Full Name">
              <input style={inputStyle} placeholder="John Doe" value={formData.full_name || ''}
                onChange={e => setFormData(p => ({ ...p, full_name: e.target.value }))}
                onFocus={e => e.target.style.borderColor = ACCENT} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </FormField>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#374151' }}>
              <input type="checkbox" checked={!!formData.is_admin} onChange={e => setFormData(p => ({ ...p, is_admin: e.target.checked }))} style={{ width: 16, height: 16, accentColor: ACCENT }} />
              Grant Admin privileges
            </label>
            <ModalFooter onClose={closeModal} onSubmit={handleSubmit} submitting={submitting} label="Create User" />
          </div>
        </Modal>
      )}
      {modal?.type === 'edit' && (
        <Modal title={`Edit @${modal.user.username}`} onClose={closeModal}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormField label="Full Name">
              <input style={inputStyle} placeholder="Full Name" value={formData.full_name || ''}
                onChange={e => setFormData(p => ({ ...p, full_name: e.target.value }))}
                onFocus={e => e.target.style.borderColor = ACCENT} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </FormField>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#374151' }}>
              <input type="checkbox" checked={!!formData.is_admin} onChange={e => setFormData(p => ({ ...p, is_admin: e.target.checked }))} style={{ width: 16, height: 16, accentColor: ACCENT }} />
              Admin privileges
            </label>
            <ModalFooter onClose={closeModal} onSubmit={handleSubmit} submitting={submitting} label="Save Changes" />
          </div>
        </Modal>
      )}
      {modal?.type === 'reset' && (
        <Modal title={`Reset Password - @${modal.user.username}`} onClose={closeModal}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormField label="New Password *">
              <input style={inputStyle} type="password" placeholder="••••••••" value={formData.new_password || ''}
                onChange={e => setFormData(p => ({ ...p, new_password: e.target.value }))}
                onFocus={e => e.target.style.borderColor = ACCENT} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </FormField>
            <ModalFooter onClose={closeModal} onSubmit={handleSubmit} submitting={submitting} label="Reset Password" />
          </div>
        </Modal>
      )}
    </div>
  );
};

// LOGS SECTION
const LogsSection = ({ apiCall, showToast }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const intervalRef = useRef(null);
  const EVENT_TYPES = ['all', 'login', 'login_failed', 'user_created', 'user_updated', 'user_deleted', 'password_reset', 'chat_message'];

  const fetchLogs = useCallback(async () => {
    try { const data = await apiCall(`${API_BASE}/admin/logs?limit=100`); setLogs(Array.isArray(data) ? data : []); setError(null); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [apiCall]);

  useEffect(() => {
    fetchLogs();
    intervalRef.current = setInterval(fetchLogs, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchLogs]);

  const filtered = filter === 'all' ? logs : logs.filter(l => l.event_type === filter);
  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          style={{
            ...inputStyle, width: 'auto', paddingRight: 32, cursor: 'pointer',
          }}
          onFocus={e => e.target.style.borderColor = ACCENT} onBlur={e => e.target.style.borderColor = '#e2e8f0'}
        >
          {EVENT_TYPES.map(t => <option key={t} value={t}>{t === 'all' ? 'All Events' : t.replace(/_/g, ' ')}</option>)}
        </select>
        <button style={btnSecondary} onClick={fetchLogs}
          onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
          onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
        >&#128260; Refresh</button>
        <div style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>
          Auto-refreshes every 30s &bull; <strong>{filtered.length}</strong> events
        </div>
      </div>
      {error && <ErrorBanner message={error} onRetry={fetchLogs} />}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f0f0f0' }}>
              <Th>Time</Th><Th>Event</Th><Th>User</Th><Th>IP Address</Th><Th>Detail</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>No logs found</td></tr>}
            {filtered.map(log => {
              const ev = EVENT_COLORS[log.event_type] || { bg: '#f3f4f6', color: '#374151' };
              return (
                <tr key={log.id} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Td><span title={log.timestamp} style={{ color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' }}>{log.timestamp ? relativeTime(log.timestamp) : '-'}</span></Td>
                  <Td><span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: ev.bg, color: ev.color, whiteSpace: 'nowrap' }}>{log.event_type?.replace(/_/g, ' ') || '-'}</span></Td>
                  <Td>{log.username ? <span style={{ fontWeight: 600, color: ACCENT }}>@{log.username}</span> : <span style={{ color: '#ccc' }}>-</span>}</Td>
                  <Td><span style={{ fontFamily: 'monospace', fontSize: 12, color: '#6b7280' }}>{log.ip_address || '-'}</span></Td>
                  <Td><span style={{ color: '#374151', maxWidth: 280, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.detail}>{log.detail || '-'}</span></Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// KEYFRAME INJECTION
const injectStyles = () => {
  if (document.getElementById('admin-dashboard-styles')) return;
  const style = document.createElement('style');
  style.id = 'admin-dashboard-styles';
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes slideIn { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: translateX(0); } }
    * { box-sizing: border-box; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 3px; }
    ::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
  `;
  document.head.appendChild(style);
};

const SECTIONS = [
  { key: 'overview', label: 'Overview',  icon: '&#128202;' },
  { key: 'users',    label: 'Users',     icon: '&#128101;' },
  { key: 'logs',     label: 'Logs',      icon: '&#128203;' },
];

// MAIN COMPONENT
const AdminDashboard = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [toasts, setToasts] = useState([]);

  useEffect(() => { injectStyles(); }, []);

  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const apiCall = useCallback(async (url, options = {}) => {
    const { isFormData, ...rest } = options;
    const headers = { Authorization: `Bearer ${token}` };
    if (!isFormData) headers['Content-Type'] = 'application/json';
    const res = await fetch(url, { ...rest, headers });
    if (res.status === 401 || res.status === 403) {
      logout(); navigate('/login');
      throw new Error('Session expired. Please log in again.');
    }
    if (!res.ok) {
      let errMsg = `Request failed (${res.status})`;
      try { const body = await res.json(); errMsg = body.detail || body.message || errMsg; } catch (_) {}
      throw new Error(errMsg);
    }
    const text = await res.text();
    if (!text) return null;
    try { return JSON.parse(text); } catch (_) { return text; }
  }, [token, logout, navigate]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const sectionTitles = { overview: 'Overview', users: 'User Management', logs: 'System Logs' };

  return (
    <div style={{
      display: 'flex', height: '100vh', overflow: 'hidden',
      fontFamily: 'var(--font-base)',
      backgroundColor: '#f1f5f9',
    }}>
      {/* SIDEBAR */}
      <div style={{
        width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH, background: SIDEBAR_BG,
        display: 'flex', flexDirection: 'column', padding: '0 0 20px 0',
        boxShadow: '4px 0 24px rgba(0,0,0,0.18)', zIndex: 100, overflowY: 'auto',
      }}>
        <div style={{ padding: '28px 22px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: `linear-gradient(135deg, ${ACCENT}, #7c3aed)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, boxShadow: '0 4px 12px rgba(79,142,247,0.4)',
            }}>&#129302;</div>
            <div>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 15, letterSpacing: '-0.3px' }}>alexAI</div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: 500 }}>Admin Panel</div>
            </div>
          </div>
        </div>
        <nav style={{ flex: 1, padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0 10px 8px' }}>Navigation</div>
          {SECTIONS.map(({ key, label, icon }) => {
            const isActive = activeSection === key;
            return (
              <button key={key} onClick={() => setActiveSection(key)} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                borderRadius: 9, border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%',
                background: isActive ? `${ACCENT}22` : 'transparent',
                color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
                fontWeight: isActive ? 700 : 500, fontSize: 14, transition: 'all 0.15s ease',
                borderLeft: isActive ? `3px solid ${ACCENT}` : '3px solid transparent',
                fontFamily: 'system-ui, -apple-system, sans-serif',
              }}
                onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; } }}
                onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; } }}
              >
                <span style={{ fontSize: 18, width: 22, textAlign: 'center', flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: icon }} />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>
        <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button onClick={() => navigate('/')} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderRadius: 8, border: 'none',
            background: 'transparent', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 13, fontWeight: 500, width: '100%',
            transition: 'all 0.15s', fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
          >&#128172; Back to Chat</button>
          <button onClick={handleLogout} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderRadius: 8, border: 'none',
            background: 'transparent', color: 'rgba(255,100,100,0.7)', cursor: 'pointer', fontSize: 13, fontWeight: 500, width: '100%',
            transition: 'all 0.15s', fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,100,100,0.7)'; }}
          >&#128694; Logout</button>
        </div>
      </div>

      {/* CONTENT */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{
          background: '#fff', padding: '0 32px', borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', gap: 16, height: 64, flexShrink: 0,
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111', letterSpacing: '-0.4px' }}>{sectionTitles[activeSection]}</h1>
          <div style={{ marginLeft: 'auto' }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%',
              background: `linear-gradient(135deg, ${ACCENT}, #7c3aed)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, color: '#fff', fontWeight: 800,
            }}>A</div>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
          {activeSection === 'overview' && <OverviewSection apiCall={apiCall} />}
          {activeSection === 'users'    && <UsersSection apiCall={apiCall} showToast={showToast} />}
          {activeSection === 'logs'     && <LogsSection apiCall={apiCall} showToast={showToast} />}
        </div>
      </div>

      {/* TOASTS */}
      <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none' }}>
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} onDismiss={() => dismissToast(t.id)} />
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;
